import { createAsyncThunk } from '@reduxjs/toolkit';
import CalendarService from '@/services/CalendarService';
import ContactsService from '@/services/ContactsService';
import DevelopmentsService from '@/services/DevelopmentsService';
import FileManagerService from '@/services/FileManagerService';
import {
  createAbortError,
  createSlotLimiter,
  getUploadPercent,
  uploadParts,
} from '@/services/fileManager/multipartUpload';
import {
  FILE_MANAGER_PAGE_SIZE,
  LINKED_ENTITY_TYPE,
  LINKED_FIELD_BY_ENTITY_KEY,
  buildFileItemEntitiesPayload,
  buildFileManagerSearchParams,
  buildListQueryKey,
  buildOrderBy,
  hasActiveFileManagerFilters,
  mapAncestorFoldersToLocationTrail,
  mapAncestorsToFolderTrail,
  mapApiEntityTypeToUrlSegment,
  mapEnrichedLinkedEntitiesToApiRefs,
  mapEntityKeyToApiType,
  mapFileItem,
  mapFileItemDetail,
  mapLinkedEntitiesToModalState,
  mergeLinkedEntityRefs,
  normalizeLinkedEntities,
  pickLinkedEntityAnchor,
  mapViewModeToApi,
} from './utils';

// Contacts have no public flag, so only properties and developments need it.
function hasLinkMetadata(entity) {
  if (entity.entity_type === LINKED_ENTITY_TYPE.CONTACT) {
    return true;
  }

  return entity.is_public != null && entity.role != null;
}

async function resolveFileItemLinkedEntities(itemId, linkedEntityRefs = []) {
  const normalized = normalizeLinkedEntities(linkedEntityRefs);
  const entityFilesCache = new Map();

  async function getCachedEntityFiles(segment, entityId) {
    const cacheKey = `${segment}:${entityId}`;

    if (entityFilesCache.has(cacheKey)) {
      return entityFilesCache.get(cacheKey);
    }

    const rows = await FileManagerService.getEntityFiles(segment, entityId);
    entityFilesCache.set(cacheKey, rows);

    return rows;
  }

  if (!normalized.length) {
    return {
      linkedEntities: mapLinkedEntitiesToModalState([]),
      apiLinkedEntities: [],
    };
  }

  // The item already carries every link's flag and role: nothing to fetch.
  if (normalized.every(hasLinkMetadata)) {
    return {
      linkedEntities: mapLinkedEntitiesToModalState(normalized),
      apiLinkedEntities: mapEnrichedLinkedEntitiesToApiRefs(normalized),
    };
  }

  let entities = normalized;
  const anchor = pickLinkedEntityAnchor(linkedEntityRefs);

  if (anchor) {
    const anchorSegment = mapApiEntityTypeToUrlSegment(anchor.entity_type);

    if (anchorSegment) {
      try {
        const rows = await getCachedEntityFiles(
          anchorSegment,
          anchor.entity_id
        );
        const match = rows.find((row) => row?.file?.id === itemId);

        if (match) {
          entities = mergeLinkedEntityRefs(
            normalized,
            match.other_links ?? [],
            [
              {
                ...anchor,
                is_public: match.is_public,
                role: match.role,
              },
            ]
          );
        }
      } catch {
        entities = normalized;
      }
    }
  }

  const enriched = await Promise.all(
    entities.map(async (entity) => {
      if (entity.entity_type === LINKED_ENTITY_TYPE.CONTACT) {
        return entity;
      }

      if (hasLinkMetadata(entity)) {
        return {
          ...entity,
          is_public: Boolean(entity.is_public),
        };
      }

      const segment = mapApiEntityTypeToUrlSegment(entity.entity_type);

      if (!segment) {
        return entity;
      }

      try {
        const rows = await getCachedEntityFiles(segment, entity.entity_id);
        const match = rows.find((row) => row?.file?.id === itemId);

        return {
          ...entity,
          is_public: Boolean(match?.is_public ?? entity.is_public),
          role: match?.role ?? entity.role,
        };
      } catch {
        return entity;
      }
    })
  );

  return {
    linkedEntities: mapLinkedEntitiesToModalState(enriched),
    apiLinkedEntities: mapEnrichedLinkedEntitiesToApiRefs(enriched),
  };
}

const createEntitySearchThunk = (
  name,
  entityKey,
  asyncFunction,
  modalKey = 'detailModal'
) =>
  createAsyncThunk(
    `FileManager/${name}`,
    async (query, { getState, rejectWithValue }) => {
      const requestId =
        getState().FileManagerSlice[modalKey][entityKey].searchRequestId;

      try {
        const data = await asyncFunction(query);

        return {
          entityKey,
          requestId,
          data,
        };
      } catch (error) {
        return rejectWithValue({ requestId });
      }
    }
  );

export const getDetailModalContacts = createEntitySearchThunk(
  'getDetailModalContacts',
  'contacts',
  ContactsService.getContacts
);

export const getDetailModalProperties = createEntitySearchThunk(
  'getDetailModalProperties',
  'properties',
  CalendarService.getProperties
);

export const getDetailModalDevelopments = createEntitySearchThunk(
  'getDetailModalDevelopments',
  'developments',
  DevelopmentsService.getCompanyDevelopments
);

export const getBulkLinkModalContacts = createEntitySearchThunk(
  'getBulkLinkModalContacts',
  'contacts',
  ContactsService.getContacts,
  'bulkLinkModal'
);

export const getBulkLinkModalProperties = createEntitySearchThunk(
  'getBulkLinkModalProperties',
  'properties',
  CalendarService.getProperties,
  'bulkLinkModal'
);

export const getBulkLinkModalDevelopments = createEntitySearchThunk(
  'getBulkLinkModalDevelopments',
  'developments',
  DevelopmentsService.getCompanyDevelopments,
  'bulkLinkModal'
);

const VALID_VIEW_MODES = ['list', 'grid'];
let persistViewAbortController = null;

export const fetchFileItems = createAsyncThunk(
  'FileManager/fetchFileItems',
  async (_, { getState, rejectWithValue }) => {
    const fileManagerState = getState().FileManagerSlice;
    const {
      currentFolderId,
      sortBy,
      sortDirection,
      searchQuery,
      uploadedBy,
      uploadDateRange,
      linkedContacts,
      linkedProperties,
      linkedDevelopments,
      matchMode,
      page,
    } = fileManagerState;
    const queryKey = buildListQueryKey({
      currentFolderId,
      sortBy,
      sortDirection,
      searchQuery,
      uploadedBy,
      uploadDateRange,
      linkedContacts,
      linkedProperties,
      linkedDevelopments,
      matchMode,
      page,
    });

    try {
      const response = hasActiveFileManagerFilters(fileManagerState)
        ? await FileManagerService.searchItems(
            buildFileManagerSearchParams({
              searchQuery,
              uploadedBy,
              uploadDateRange,
              linkedContacts,
              linkedProperties,
              linkedDevelopments,
              matchMode,
              sortBy,
              sortDirection,
              page,
              pageSize: FILE_MANAGER_PAGE_SIZE,
            })
          )
        : await FileManagerService.getItems({
            parentId: currentFolderId,
            orderBy: buildOrderBy(sortBy, sortDirection),
            page,
            pageSize: FILE_MANAGER_PAGE_SIZE,
          });

      return {
        items: (response.results ?? []).map(mapFileItem),
        total: response.total ?? 0,
        page: response.page ?? 1,
        pageSize: response.page_size ?? FILE_MANAGER_PAGE_SIZE,
        queryKey,
      };
    } catch (error) {
      return rejectWithValue({ queryKey, error });
    }
  }
);

export const fetchFileItemLinkedEntities = createAsyncThunk(
  'FileManager/fetchFileItemLinkedEntities',
  async ({ itemId, linkedEntityRefs }, { rejectWithValue }) => {
    try {
      let refs = linkedEntityRefs;

      if (!refs) {
        const apiItem = await FileManagerService.getItem(itemId);
        refs = apiItem.linked_entities;
      }

      const resolved = await resolveFileItemLinkedEntities(itemId, refs);

      return {
        itemId,
        ...resolved,
      };
    } catch (error) {
      return rejectWithValue({ itemId, error });
    }
  }
);

export const fetchFileItemDetail = createAsyncThunk(
  'FileManager/fetchFileItemDetail',
  async (itemId, { dispatch, getState, rejectWithValue }) => {
    try {
      const apiItem = await FileManagerService.getItem(itemId);
      const item = mapFileItemDetail(apiItem);

      // Reuse this response so the linked entities don't request the item again.
      dispatch(
        fetchFileItemLinkedEntities({
          itemId,
          linkedEntityRefs: apiItem.linked_entities ?? [],
        })
      );

      let locationTrail = getState().FileManagerSlice.folderTrail ?? [];

      try {
        const ancestorsResponse = await FileManagerService.getItemAncestors(
          itemId
        );
        const ancestorTrail = mapAncestorFoldersToLocationTrail(
          ancestorsResponse.results ?? []
        );

        if (ancestorTrail.length > 0) {
          locationTrail = ancestorTrail;
        }
      } catch {
        // Keep the current folder trail as a fallback when ancestors fail.
      }

      return {
        itemId,
        item,
        locationTrail,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const enterFolderFromFilteredResults = createAsyncThunk(
  'FileManager/enterFolderFromFilteredResults',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const response = await FileManagerService.getItemAncestors(id);
      const folderTrail = mapAncestorsToFolderTrail(response.results ?? [], {
        id,
        name,
      });

      return { id, folderTrail };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const persistFileManagerViewMode = createAsyncThunk(
  'FileManager/persistFileManagerViewMode',
  async (viewMode, { rejectWithValue }) => {
    if (persistViewAbortController) {
      persistViewAbortController.abort();
    }

    const abortController = new AbortController();
    persistViewAbortController = abortController;
    const apiViewMode = mapViewModeToApi(viewMode);

    try {
      const response = await FileManagerService.updateViewPreference(
        apiViewMode,
        { signal: abortController.signal }
      );
      return response;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }

      return rejectWithValue(error);
    } finally {
      if (persistViewAbortController === abortController) {
        persistViewAbortController = null;
      }
    }
  },
  {
    condition: (viewMode, { getState }) => {
      if (!VALID_VIEW_MODES.includes(viewMode)) {
        return false;
      }

      const { viewMode: currentViewMode } = getState().FileManagerSlice;
      return viewMode !== currentViewMode;
    },
  }
);

export const createFileFolder = createAsyncThunk(
  'FileManager/createFileFolder',
  async (name, { getState, dispatch, rejectWithValue }) => {
    const { currentFolderId } = getState().FileManagerSlice;
    const trimmedName = name.trim();

    if (!trimmedName) {
      return rejectWithValue('empty_name');
    }

    try {
      const response = await FileManagerService.createFolder({
        name: trimmedName,
        parentId: currentFolderId,
      });

      await dispatch(fetchFileItems());

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const checkUploadDuplicates = createAsyncThunk(
  'FileManager/checkUploadDuplicates',
  async ({ items, originEntity = null }, { getState, rejectWithValue }) => {
    try {
      const response = originEntity
        ? await FileManagerService.checkEntityDuplicates({
            entityType: originEntity.entityType,
            entityId: originEntity.entityId,
            items,
          })
        : await FileManagerService.checkDuplicates({
            parentId: getState().FileManagerSlice.currentFolderId ?? null,
            items,
          });

      return response.duplicate_indexes;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createFileLink = createAsyncThunk(
  'FileManager/createFileLink',
  async (
    {
      name,
      url,
      linkedProperties = [],
      linkedDevelopments = [],
      linkedContacts = [],
      originEntity = null,
      duplicateStrategy,
    },
    { getState, dispatch, rejectWithValue }
  ) => {
    const { currentFolderId } = getState().FileManagerSlice;
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();

    if (!trimmedName || !trimmedUrl) {
      return rejectWithValue('empty_fields');
    }

    const entities = buildFileItemEntitiesPayload({
      linkedProperties,
      linkedDevelopments,
      linkedContacts,
    });

    try {
      if (originEntity) {
        return await FileManagerService.createEntityLink({
          entityType: originEntity.entityType,
          entityId: originEntity.entityId,
          name: trimmedName,
          url: trimmedUrl,
          entities,
          duplicateStrategy,
        });
      }

      const response = await FileManagerService.createLink({
        name: trimmedName,
        url: trimmedUrl,
        parentId: currentFolderId,
        entities,
        duplicateStrategy,
      });

      await dispatch(fetchFileItems());

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const compareNumbers = (a, b) => a - b;

async function reserveAndUploadFile({
  file,
  progress,
  onProgress,
  onUploadProgress,
  reserve,
  signal,
}) {
  let { fileItemId, partUploadUrls, partSize, totalParts, completedItem } =
    progress;
  let uploadedParts = [...(progress.uploadedParts ?? [])];

  if (!fileItemId) {
    const reservation = await reserve();
    fileItemId = reservation.file_item.id;
    partUploadUrls = reservation.part_upload_urls;
    partSize = reservation.part_size;
    totalParts = reservation.total_parts;
    uploadedParts = [];
    onProgress({
      fileItemId,
      partUploadUrls,
      partSize,
      totalParts,
      uploadedParts: [],
    });
  }

  if (!completedItem) {
    if (uploadedParts.length < totalParts) {
      const recorded = new Set(uploadedParts);
      uploadedParts = await uploadParts({
        file,
        partSize,
        partUploadUrls,
        uploadedParts,
        signal,
        putPart: (url, body, options) =>
          FileManagerService.putUploadPart(url, body, options),
        onProgress: onUploadProgress,
        onPartUploaded: (partNumber) => {
          recorded.add(partNumber);
          onProgress({ uploadedParts: [...recorded].sort(compareNumbers) });
        },
      });
    }

    // Parts are in: cancelling now must not complete (and publish) the file.
    if (signal?.aborted) {
      throw createAbortError();
    }

    onUploadProgress({ uploadedBytes: file.size, totalBytes: file.size });

    try {
      completedItem = await FileManagerService.completeUpload(fileItemId);
    } catch (error) {
      if (error?.status === 400) {
        onProgress({ uploadedParts: [] });
      }
      throw error;
    }

    onProgress({ completedItem });
  }

  return { fileItemId, completedItem };
}

const uploadFileToEntity = async ({
  file,
  originEntity,
  linkedProperties = [],
  linkedDevelopments = [],
  linkedContacts = [],
  progress = {},
  onProgress = () => {},
  onUploadProgress = () => {},
  signal,
  duplicateStrategy,
}) => {
  const { completedItem } = await reserveAndUploadFile({
    file,
    progress,
    onProgress,
    onUploadProgress,
    signal,
    reserve: () =>
      FileManagerService.initEntityUpload({
        entityType: originEntity.entityType,
        entityId: originEntity.entityId,
        file,
        entities: buildFileItemEntitiesPayload({
          linkedProperties,
          linkedDevelopments,
          linkedContacts,
        }),
        duplicateStrategy,
      }),
  });

  return completedItem;
};

// Each step reports through onProgress so a retry with the same file can
// resume from the step that failed instead of reserving a duplicate item.
// Entity links are created atomically by upload-init, before any byte.
export const uploadEntityFile = createAsyncThunk(
  'FileManager/uploadEntityFile',
  async (
    {
      file,
      originEntity,
      linkedProperties = [],
      linkedDevelopments = [],
      linkedContacts = [],
      progress = {},
      onProgress = () => {},
      onUploadProgress = () => {},
      duplicateStrategy,
    },
    { rejectWithValue }
  ) => {
    if (!file || !originEntity) {
      return rejectWithValue('missing_file_or_origin');
    }

    try {
      return await uploadFileToEntity({
        file,
        originEntity,
        linkedProperties,
        linkedDevelopments,
        linkedContacts,
        progress,
        onProgress,
        onUploadProgress,
        duplicateStrategy,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Folder upload-init does not accept entities, so links are created after
// upload-complete, one by one.
const uploadFileToFolder = async ({
  file,
  parentId,
  linkedProperties = [],
  linkedDevelopments = [],
  linkedContacts = [],
  onProgress = () => {},
  onUploadProgress = () => {},
  signal,
  duplicateStrategy,
}) => {
  const { fileItemId, completedItem } = await reserveAndUploadFile({
    file,
    progress: {},
    onProgress,
    onUploadProgress,
    signal,
    reserve: () =>
      FileManagerService.initUpload({ file, parentId, duplicateStrategy }),
  });

  const entities = buildFileItemEntitiesPayload({
    linkedProperties,
    linkedDevelopments,
    linkedContacts,
  });

  for (let index = 0; index < entities.length; index += 1) {
    const entity = entities[index];
    // eslint-disable-next-line no-await-in-loop
    await FileManagerService.linkItemEntity(fileItemId, {
      entityType: entity.entity_type,
      entityId: entity.entity_id,
      isPublic: entity.is_public,
    });
  }

  return completedItem;
};

export const discardEntityUpload = createAsyncThunk(
  'FileManager/discardEntityUpload',
  async ({ fileItemId }) => {
    try {
      await FileManagerService.abortUpload(fileItemId);
    } catch {
      // Best effort: an abandoned PENDING item is never listed anywhere.
    }
  }
);

export const MAX_CONCURRENT_BATCH_FILES = 10;

export const BATCH_FILE_STATUS = {
  UPLOADED: 'uploaded',
  UNLINKED: 'unlinked',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Uploads several files independently: one failure never stops the others.
// A failed file's PENDING reservation is aborted so the user can simply upload
// it again. A file that reached READY but failed a link is reported apart as
// unlinked: uploading it again would create a duplicate.
// Without originEntity the files go to parentId.
// A file cancelled through its signal (getSignal) is dropped silently: its
// reservation is aborted and it is neither uploaded nor failed.
// Resolves with one BATCH_FILE_STATUS per file, in the same order; each one is
// also reported through onFileStatus as soon as that file settles.
export async function uploadFilesToTarget({
  files,
  originEntity = null,
  parentId = null,
  linkedProperties = [],
  linkedDevelopments = [],
  linkedContacts = [],
  getSignal = () => undefined,
  onFileProgress = () => {},
  onFileStatus = () => {},
  duplicateStrategy,
  duplicateFiles = [],
  limiter,
}) {
  const uploadOne = async (file) => {
    const signal = getSignal(file);
    const progress = {};
    const upload = {
      file,
      linkedProperties,
      linkedDevelopments,
      linkedContacts,
      signal,
      duplicateStrategy: duplicateFiles.includes(file)
        ? duplicateStrategy
        : undefined,
      onProgress: (update) => Object.assign(progress, update),
      onUploadProgress: ({ uploadedBytes, totalBytes }) =>
        onFileProgress(file, getUploadPercent(uploadedBytes, totalBytes)),
    };
    let release = () => {};

    const settle = (status) => {
      onFileStatus(file, status);
      return status;
    };

    try {
      release = await limiter.acquire(signal);

      if (originEntity) {
        await uploadFileToEntity({ ...upload, originEntity });
      } else {
        await uploadFileToFolder({ ...upload, parentId });
      }
      return settle(BATCH_FILE_STATUS.UPLOADED);
    } catch {
      if (progress.completedItem) {
        return settle(BATCH_FILE_STATUS.UNLINKED);
      }
      if (progress.fileItemId) {
        await FileManagerService.abortUpload(progress.fileItemId).catch(
          () => {}
        );
      }
      return settle(
        signal?.aborted ? BATCH_FILE_STATUS.CANCELLED : BATCH_FILE_STATUS.FAILED
      );
    } finally {
      release();
    }
  };

  return Promise.all(files.map(uploadOne));
}

// Batch upload of the AddFile modal. Without originEntity the files go to the
// current folder and the list is refreshed once for the whole batch.
export const uploadFileBatch = createAsyncThunk(
  'FileManager/uploadFileBatch',
  async (
    {
      files = [],
      originEntity = null,
      linkedProperties = [],
      linkedDevelopments = [],
      linkedContacts = [],
      getSignal = () => undefined,
      onFileProgress = () => {},
      duplicateStrategy,
      duplicateFiles = [],
    },
    { getState, dispatch, rejectWithValue }
  ) => {
    if (files.length === 0) {
      return rejectWithValue('missing_files');
    }

    const statuses = await uploadFilesToTarget({
      files,
      originEntity,
      parentId: getState().FileManagerSlice.currentFolderId ?? null,
      linkedProperties,
      linkedDevelopments,
      linkedContacts,
      getSignal,
      onFileProgress,
      duplicateStrategy,
      duplicateFiles,
      limiter: createSlotLimiter(MAX_CONCURRENT_BATCH_FILES),
    });

    if (!originEntity) {
      await dispatch(fetchFileItems());
    }

    const namesWith = (status) =>
      files
        .filter((_, index) => statuses[index] === status)
        .map((file) => file.name);

    return {
      uploadedFileNames: namesWith(BATCH_FILE_STATUS.UPLOADED),
      unlinkedFileNames: namesWith(BATCH_FILE_STATUS.UNLINKED),
      failedFileNames: namesWith(BATCH_FILE_STATUS.FAILED),
      allCancelled: statuses.every(
        (status) => status === BATCH_FILE_STATUS.CANCELLED
      ),
    };
  }
);

export const updateFileItemDetails = createAsyncThunk(
  'FileManager/updateFileItemDetails',
  async ({ id, name, description, field }, { rejectWithValue }) => {
    const payload = { itemId: id };

    if (field === 'name') {
      const trimmedName = name?.trim() ?? '';

      if (!trimmedName) {
        return rejectWithValue({ reason: 'empty_name', field: 'name' });
      }

      payload.name = trimmedName;
    }

    if (field === 'description') {
      payload.description = (description ?? '').trim();
    }

    try {
      const response = await FileManagerService.updateItem(payload);

      return {
        id,
        field,
        response,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const renameFileItem = createAsyncThunk(
  'FileManager/renameFileItem',
  async ({ id, name }, { dispatch, rejectWithValue }) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return rejectWithValue('empty_name');
    }

    try {
      const response = await FileManagerService.renameItem({
        itemId: id,
        name: trimmedName,
      });

      await dispatch(fetchFileItems());

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const moveFileItem = createAsyncThunk(
  'FileManager/moveFileItem',
  async ({ id, newParentId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await FileManagerService.moveItem({
        itemId: id,
        newParentId,
      });

      await dispatch(fetchFileItems());

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteFileItems = createAsyncThunk(
  'FileManager/deleteFileItems',
  async ({ ids }, { dispatch, getState, rejectWithValue }) => {
    try {
      if (ids.length === 1) {
        await FileManagerService.deleteItem(ids[0]);
      } else {
        await FileManagerService.bulkDeleteItems(ids);
      }
    } catch (error) {
      return rejectWithValue(error);
    }

    const { page, pageSize, total } = getState().FileManagerSlice;
    const emptiedPage = page > 1 && total - ids.length <= (page - 1) * pageSize;

    // Only the last page can be emptied: later items refill a middle one.
    // An emptied page steps back in the reducer, which refetches via the
    // dashboard's page effect; otherwise reload the current page here.
    if (!emptiedPage) {
      await dispatch(fetchFileItems());
    }

    return { ids, emptiedPage };
  }
);

function isEntityAlreadyLinked(linkedEntities, entityKey, itemId) {
  const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];
  const linkedList = linkedEntities?.[linkedField] ?? [];

  return linkedList.some((current) => current?.id === itemId);
}

export const linkFileItemEntity = createAsyncThunk(
  'FileManager/linkFileItemEntity',
  async ({ entityKey, item }, { getState, rejectWithValue }) => {
    const { detailModal } = getState().FileManagerSlice;
    const { itemId } = detailModal;

    if (!itemId || !item?.id || !entityKey) {
      return rejectWithValue({ reason: 'invalid_payload', itemId });
    }

    if (isEntityAlreadyLinked(detailModal.linkedEntities, entityKey, item.id)) {
      return rejectWithValue({ reason: 'already_linked', itemId });
    }

    const entityType = mapEntityKeyToApiType(entityKey);

    if (!entityType) {
      return rejectWithValue({ reason: 'invalid_entity_key', itemId });
    }

    const showInWeb =
      entityKey === 'contacts' ? undefined : Boolean(item.showInWeb);

    try {
      await FileManagerService.linkItemEntity(itemId, {
        entityType,
        entityId: item.id,
        isPublic: showInWeb,
        role: '',
      });

      const apiItem = await FileManagerService.getItem(itemId);
      const resolved = await resolveFileItemLinkedEntities(
        itemId,
        apiItem.linked_entities
      );

      return {
        itemId,
        ...resolved,
      };
    } catch (error) {
      return rejectWithValue({ itemId, error });
    }
  }
);

export const unlinkFileItemEntity = createAsyncThunk(
  'FileManager/unlinkFileItemEntity',
  async ({ entityKey, item }, { getState, rejectWithValue }) => {
    const { detailModal } = getState().FileManagerSlice;
    const { itemId } = detailModal;
    const entityId = item?.id;

    if (!itemId || entityId == null || !entityKey) {
      return rejectWithValue({ reason: 'invalid_payload', itemId });
    }

    const entityType = mapEntityKeyToApiType(entityKey);

    if (!entityType) {
      return rejectWithValue({ reason: 'invalid_entity_key', itemId });
    }

    try {
      await FileManagerService.unlinkItemEntity(itemId, {
        entityType,
        entityId,
      });

      const apiItem = await FileManagerService.getItem(itemId);
      const resolved = await resolveFileItemLinkedEntities(
        itemId,
        apiItem.linked_entities
      );

      return {
        itemId,
        ...resolved,
      };
    } catch (error) {
      return rejectWithValue({ itemId, error });
    }
  }
);

export const toggleFileItemEntityPublic = createAsyncThunk(
  'FileManager/toggleFileItemEntityPublic',
  async ({ entityKey, itemId: entityId }, { getState, rejectWithValue }) => {
    const { detailModal } = getState().FileManagerSlice;
    const fileItemId = detailModal.itemId;
    const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];
    const linkedList = detailModal.linkedEntities?.[linkedField] ?? [];
    const linkedItem = linkedList.find((current) => current?.id === entityId);

    if (!fileItemId || !linkedItem || !entityKey) {
      return rejectWithValue({ reason: 'invalid_payload', itemId: fileItemId });
    }

    if (entityKey !== 'properties' && entityKey !== 'developments') {
      return rejectWithValue({
        reason: 'invalid_entity_key',
        itemId: fileItemId,
      });
    }

    const entityType = mapEntityKeyToApiType(entityKey);
    const nextShowInWeb = !linkedItem.showInWeb;

    try {
      await FileManagerService.linkItemEntity(fileItemId, {
        entityType,
        entityId,
        isPublic: nextShowInWeb,
        role: linkedItem.role ?? '',
      });

      const apiItem = await FileManagerService.getItem(fileItemId);
      const resolved = await resolveFileItemLinkedEntities(
        fileItemId,
        apiItem.linked_entities
      );

      return {
        itemId: fileItemId,
        ...resolved,
      };
    } catch (error) {
      return rejectWithValue({ itemId: fileItemId, error });
    }
  }
);

function getBulkLinkEntitiesList(linkedEntities) {
  const { linkedProperties, linkedDevelopments, linkedContacts } =
    linkedEntities ?? {};

  return [
    ...(linkedProperties ?? []).map((item) => ({
      entityKey: 'properties',
      item,
    })),
    ...(linkedDevelopments ?? []).map((item) => ({
      entityKey: 'developments',
      item,
    })),
    ...(linkedContacts ?? []).map((item) => ({ entityKey: 'contacts', item })),
  ];
}

function isItemLinkedToEntity(item, entityType, entityId) {
  return (item?.linkedEntities ?? []).some(
    (entity) =>
      entity.entity_type === entityType &&
      String(entity.entity_id) === String(entityId)
  );
}

export const BULK_LINK_BATCH_SIZE = 20;

export async function runOperationsInBatches(operations, batchSize) {
  const results = [];

  for (let index = 0; index < operations.length; index += batchSize) {
    const batch = operations.slice(index, index + batchSize);
    // Sequential batches are intentional to cap request concurrency.
    // eslint-disable-next-line no-await-in-loop
    const batchResults = await Promise.allSettled(
      batch.map((operation) => operation.execute())
    );

    batchResults.forEach((result, batchIndex) => {
      results.push({
        result,
        itemId: batch[batchIndex].itemId,
      });
    });
  }

  return results;
}

function buildBulkLinkOperations(selectedItems, entities, mode) {
  const operations = [];

  selectedItems.forEach((fileItem) => {
    entities.forEach(({ entityKey, item: entity }) => {
      const entityType = mapEntityKeyToApiType(entityKey);
      const entityId = entity?.id;

      if (!entityType || entityId == null) {
        return;
      }

      const isLinked = isItemLinkedToEntity(fileItem, entityType, entityId);

      if (mode === 'unlink' && !isLinked) {
        return;
      }

      const showInWeb =
        entityKey === 'contacts' ? undefined : Boolean(entity.showInWeb);

      operations.push({
        itemId: fileItem.id,
        execute: () =>
          mode === 'link'
            ? FileManagerService.linkItemEntity(fileItem.id, {
                entityType,
                entityId,
                isPublic: showInWeb,
                role: '',
              })
            : FileManagerService.unlinkItemEntity(fileItem.id, {
                entityType,
                entityId,
              }),
      });
    });
  });

  return operations;
}

export const bulkLinkFileItemEntities = createAsyncThunk(
  'FileManager/bulkLinkFileItemEntities',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { bulkLinkModal } = getState().FileManagerSlice;
    const { itemIds, mode, linkedEntities } = bulkLinkModal;
    const entities = getBulkLinkEntitiesList(linkedEntities);

    if (!itemIds?.length || !entities.length) {
      return rejectWithValue({ reason: 'invalid_payload' });
    }

    await dispatch(fetchFileItems());

    const { items } = getState().FileManagerSlice;
    const selectedItems = items.filter((item) => itemIds.includes(item.id));
    const operations = buildBulkLinkOperations(selectedItems, entities, mode);

    if (!operations.length) {
      return { total: 0, failed: 0, failedItems: 0, succeeded: 0 };
    }

    const results = await runOperationsInBatches(
      operations,
      BULK_LINK_BATCH_SIZE
    );
    const failedResults = results.filter(
      ({ result }) => result.status === 'rejected'
    );
    const failed = failedResults.length;
    const failedItems = new Set(failedResults.map(({ itemId }) => itemId)).size;
    const total = results.length;

    if (failed === total) {
      return rejectWithValue({
        reason: 'all_failed',
        total,
        failed,
        failedItems,
      });
    }

    await dispatch(fetchFileItems());

    return {
      total,
      failed,
      failedItems,
      succeeded: total - failed,
    };
  }
);
